/**
 * Lightweight pre-upload video editor: color filters (baked on Apply in background).
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import { VIDEO_EDIT_DEFAULTS } from '../../utils/applyVideoEdits';
import { buildColorCssFilter } from '../../utils/mediaColorFilter';

export default function PostVideoEditor({
  open,
  sourceUrl,
  onClose,
  onApply,
  onSkip,
  applying = false,
  maxSeconds = 15,
}) {
  const [brightness, setBrightness] = useState(VIDEO_EDIT_DEFAULTS.brightness);
  const [contrast, setContrast] = useState(VIDEO_EDIT_DEFAULTS.contrast);
  const [saturation, setSaturation] = useState(VIDEO_EDIT_DEFAULTS.saturation);
  const [warmth, setWarmth] = useState(VIDEO_EDIT_DEFAULTS.warmth);

  useEffect(() => {
    if (!open) return;
    setBrightness(VIDEO_EDIT_DEFAULTS.brightness);
    setContrast(VIDEO_EDIT_DEFAULTS.contrast);
    setSaturation(VIDEO_EDIT_DEFAULTS.saturation);
    setWarmth(VIDEO_EDIT_DEFAULTS.warmth);
  }, [open, sourceUrl]);

  const previewFilter = buildColorCssFilter({ brightness, contrast, saturation, warmth });

  const reset = () => {
    setBrightness(0);
    setContrast(0);
    setSaturation(0);
    setWarmth(0);
  };

  return (
    <Dialog
      open={open}
      onClose={applying ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      transitionDuration={0}
      data-testid="post-video-editor"
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Adjust video</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'grey.900',
            minHeight: 240,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          {sourceUrl ? (
            <Box
              component="video"
              src={sourceUrl}
              controls
              playsInline
              muted
              autoPlay
              loop
              sx={{
                width: '100%',
                maxHeight: 360,
                display: 'block',
                filter: previewFilter,
                backgroundColor: '#000',
              }}
              data-testid="post-video-editor-preview"
            />
          ) : null}
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Clips must be ≤{maxSeconds}s. Tap Apply to close — adjustments finish in the background.
        </Typography>

        <Stack spacing={2}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight={600}>Brightness</Typography>
              <Typography variant="caption" color="text.secondary">{brightness}</Typography>
            </Stack>
            <Slider
              value={brightness}
              min={-40}
              max={40}
              step={1}
              onChange={(_, v) => setBrightness(v)}
              disabled={applying}
              aria-label="Brightness"
              data-testid="post-video-brightness"
            />
          </Box>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight={600}>Contrast</Typography>
              <Typography variant="caption" color="text.secondary">{contrast}</Typography>
            </Stack>
            <Slider
              value={contrast}
              min={-40}
              max={40}
              step={1}
              onChange={(_, v) => setContrast(v)}
              disabled={applying}
              aria-label="Contrast"
              data-testid="post-video-contrast"
            />
          </Box>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight={600}>Saturation</Typography>
              <Typography variant="caption" color="text.secondary">{saturation}</Typography>
            </Stack>
            <Slider
              value={saturation}
              min={-40}
              max={40}
              step={1}
              onChange={(_, v) => setSaturation(v)}
              disabled={applying}
              aria-label="Saturation"
              data-testid="post-video-saturation"
            />
          </Box>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight={600}>Warmth</Typography>
              <Typography variant="caption" color="text.secondary">{warmth}</Typography>
            </Stack>
            <Slider
              value={warmth}
              min={-40}
              max={40}
              step={1}
              onChange={(_, v) => setWarmth(v)}
              disabled={applying}
              aria-label="Warmth"
              data-testid="post-video-warmth"
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Tooltip title="Reset adjustments">
          <span>
            <IconButton onClick={reset} disabled={applying} aria-label="Reset adjustments">
              <RestartAlt />
            </IconButton>
          </span>
        </Tooltip>
        <Stack direction="row" spacing={1}>
          <Button onClick={onSkip} disabled={applying} sx={{ textTransform: 'none' }}>
            Skip
          </Button>
          <Button onClick={onClose} disabled={applying} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onApply?.({ brightness, contrast, saturation, warmth })}
            disabled={applying}
            sx={{ textTransform: 'none', fontWeight: 700 }}
            data-testid="post-video-editor-apply"
          >
            {applying ? 'Applying…' : 'Apply'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
